/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ReportState {
	openModalDownloadReport: boolean;
	openDrawerFilters: boolean;
	openDrawerRows: boolean;
	reportRow: any;
	reportTableParams: {
		pagination: {
			current: number;
			pageSize: number;
			showSizeChanger: boolean;
			pageSizeOptions: string[];
			total: number;
		};
	};
	reportRefreshTable: boolean;
	reportResetFilters: boolean;
	reportResetCascaderPage: boolean;
	filterType: string;
	filterLiftType: string;
	filterStartDate: any;
	filterEndDate: any;
	filterIdOrgSelected: {
		id: string;
		name: string;
	};
	filterUseCustomSearch: {
		useCustomSearch: boolean;
		type: string;
		value: string;
		showSelect: boolean;
		orgId: string;
	};
	filterAddExclusionTimeRanges: boolean;
	filterAddExclusionDateRanges: boolean;
	filterExclusionTimeRange: string[];
	filterExclusionDateRange: string[];
	filterExclusionSaturdays: boolean;
	filterExclusionSundays: boolean;
	filterExclusionHolidays: boolean;
	filterCascaderTypeLevel: number;
	filterCascaderTypeArr: {
		value: string;
	}[];
	filterCascaderTypeList: [];
	filterCascaderValue: [];
	filterTimezone: {
		tzCode: string;
		label: string;
		utc: string;
		name: string;
	};
	tableListColumns: {
		label: string;
	}[];
	tableSelectedColumns: string[];
}

const initialState: ReportState = {
	openModalDownloadReport: false,
	openDrawerFilters: false,
	openDrawerRows: false,
	reportRow: {},
	reportTableParams: {
		pagination: {
			current: 1,
			pageSize: 10,
			showSizeChanger: true,
			pageSizeOptions: ["10", "20", "50", "100"],
			total: 0,
		},
	},
	reportRefreshTable: false,
	reportResetFilters: false,
	reportResetCascaderPage: false,
	// Filters
	filterType: "Organizations",
	filterUseCustomSearch: {
		useCustomSearch: false,
		type: "",
		value: "",
		showSelect: true,
		orgId: "",
	},
	filterIdOrgSelected: { id: "", name: "" },
	filterLiftType: "ALL",
	filterStartDate: "",
	filterEndDate: "",
	filterAddExclusionTimeRanges: false,
	filterAddExclusionDateRanges: false,
	filterExclusionTimeRange: [],
	filterExclusionDateRange: [],
	filterExclusionSaturdays: false,
	filterExclusionSundays: false,
	filterExclusionHolidays: false,
	filterCascaderTypeLevel: 1,
	filterCascaderTypeArr: [],
	filterCascaderTypeList: [],
	filterCascaderValue: [],
	filterTimezone: {
		tzCode: "",
		label: "",
		utc: "",
		name: "",
	},
	tableListColumns: [],
	tableSelectedColumns: [],
};

export const reportSlice = createSlice({
	name: "report",
	initialState,
	reducers: {
		updateFilterIdOrgSelected: (state, action: PayloadAction<any>) => {
			state.filterIdOrgSelected = action.payload;
		},
		updateOpenAlertDownloadReport: (state, action: PayloadAction<boolean>) => {
			state.openModalDownloadReport = action.payload;
		},
		updateOpenDrawerFilters: (state, action: PayloadAction<boolean>) => {
			state.openDrawerFilters = action.payload;
		},
		updateReportRow: (state, action: PayloadAction<any>) => {
			state.reportRow = action.payload;
		},
		updateOpenDrawerRows: (state, action: PayloadAction<boolean>) => {
			state.openDrawerRows = action.payload;
		},
		updateReportTableParams: (state, action: PayloadAction<any>) => {
			state.reportTableParams = action.payload;
		},
		updateFilterType: (state, action: PayloadAction<string>) => {
			state.filterType = action.payload;
		},
		updateReportRefreshTable: (state, action: PayloadAction<boolean>) => {
			state.reportRefreshTable = action.payload;
		},
		updateReportResetFilters: (state, action: PayloadAction<boolean>) => {
			state.reportResetFilters = action.payload;
		},
		updateReportResetCascaderPage: (state, action: PayloadAction<boolean>) => {
			state.reportResetCascaderPage = action.payload;
		},
		updateFilterUseCustomSearch: (state, action: PayloadAction<any>) => {
			state.filterUseCustomSearch = action.payload;
		},
		updateFilterLiftType: (state, action: PayloadAction<string>) => {
			state.filterLiftType = action.payload;
		},
		updateFilterStartDate: (state, action: PayloadAction<string>) => {
			state.filterStartDate = action.payload;
		},
		updateFilterEndDate: (state, action: PayloadAction<any>) => {
			state.filterEndDate = action.payload;
		},
		updateFilterAddExclusionTimeRanges: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.filterAddExclusionTimeRanges = action.payload;
		},
		updateFilterAddExclusionDateRanges: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.filterAddExclusionDateRanges = action.payload;
		},
		updateFilterExclusionTimeRange: (state, action: PayloadAction<any>) => {
			state.filterExclusionTimeRange = action.payload;
		},
		updateFilterExclusionDateRange: (state, action: PayloadAction<any>) => {
			state.filterExclusionDateRange = action.payload;
		},
		updateFilterExclusionSaturdays: (state, action: PayloadAction<boolean>) => {
			state.filterExclusionSaturdays = action.payload;
		},
		updateFilterExclusionSundays: (state, action: PayloadAction<boolean>) => {
			state.filterExclusionSundays = action.payload;
		},
		updateFilterExclusionHolidays: (state, action: PayloadAction<boolean>) => {
			state.filterExclusionHolidays = action.payload;
		},
		updateFilterCascaderTypeLevel: (state, action: PayloadAction<number>) => {
			state.filterCascaderTypeLevel = action.payload;
		},
		updateFilterCascaderTypeArr: (state, action: PayloadAction<any>) => {
			state.filterCascaderTypeArr = action.payload;
		},
		updateFilterCascaderTypeList: (state, action: PayloadAction<any>) => {
			state.filterCascaderTypeList = action.payload;
		},
		updateFilterCascaderValue: (state, action: PayloadAction<any>) => {
			state.filterCascaderValue = action.payload;
		},
		updateFilterTimezone: (state, action: PayloadAction<any>) => {
			state.filterTimezone = action.payload;
		},
		updateTableListColumns: (state, action: PayloadAction<any>) => {
			state.tableListColumns = action.payload;
		},
		updateTableSelectedColumns: (state, action: PayloadAction<any>) => {
			state.tableSelectedColumns = action.payload;
		},
	},
});

// Action creators are generated for each case reducer function
export const {
	updateOpenAlertDownloadReport,
	updateOpenDrawerFilters,
	updateOpenDrawerRows,
	updateReportRow,
	updateReportTableParams,
	updateReportRefreshTable,
	updateReportResetFilters,
	updateReportResetCascaderPage,
	updateFilterUseCustomSearch,
	updateFilterType,
	updateFilterIdOrgSelected,
	updateFilterLiftType,
	updateFilterStartDate,
	updateFilterEndDate,
	updateFilterAddExclusionTimeRanges,
	updateFilterExclusionTimeRange,
	updateFilterAddExclusionDateRanges,
	updateFilterExclusionDateRange,
	updateFilterExclusionSaturdays,
	updateFilterExclusionSundays,
	updateFilterExclusionHolidays,
	updateFilterCascaderTypeLevel,
	updateFilterCascaderTypeArr,
	updateFilterCascaderTypeList,
	updateFilterCascaderValue,
	updateFilterTimezone,
	updateTableListColumns,
	updateTableSelectedColumns,
} = reportSlice.actions;

export default reportSlice.reducer;
